/**
 * Import zaproszeń z pliku CSV (Etap 54, ADR-0060).
 *
 * Plik niesie to samo, co formularz „Zaproś pracownika” — imię, nazwisko,
 * e-mail i plan zatrudnienia — i nic więcej. Numeru PESEL, adresu ani rachunku
 * w pliku nie ma: dane wrażliwe podaje kandydat w portalu onboardingu, gdzie są
 * szyfrowane, a nie przechodzą przez arkusz kadr.
 *
 * Moduł jest czysty: parser CSV i odczyt wierszy bez bazy. Reguły, które
 * wymagają bazy (adres z kontem, oczekujące zaproszenie), dokłada serwer.
 */
import { CONTRACT_TYPES, type ContractType } from './enums'
import { emailSchema } from './validators'

/** Najwięcej wierszy danych w jednym pliku — import to zespół, nie kartoteka. */
export const INVITATION_IMPORT_MAX_ROWS = 200
/** Rozmiar pliku w bajtach UTF-8 (256 KB mieści 200 wierszy z zapasem). */
export const INVITATION_IMPORT_MAX_BYTES = 256 * 1024

export const INVITATION_IMPORT_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'position',
  'departmentName',
  'employmentType',
  'startDate',
  'managerName',
] as const
export type InvitationImportField = (typeof INVITATION_IMPORT_FIELDS)[number]

/** Kolumny wymagane — bez nich zaproszenia nie ma do kogo wysłać. */
export const INVITATION_IMPORT_REQUIRED: readonly InvitationImportField[] = ['firstName', 'lastName', 'email']

/** Nagłówki wzoru pliku (PL) — w tej kolejności wychodzi szablon do pobrania. */
export const INVITATION_IMPORT_HEADERS: Readonly<Record<InvitationImportField, string>> = {
  firstName: 'imie',
  lastName: 'nazwisko',
  email: 'email',
  position: 'stanowisko',
  departmentName: 'dzial',
  employmentType: 'rodzaj_umowy',
  startDate: 'data_rozpoczecia',
  managerName: 'przelozony',
}

/** Najdłuższe wartości — te same granice co formularz zaproszenia. */
const MAX_LENGTH: Readonly<Partial<Record<InvitationImportField, number>>> = {
  firstName: 80,
  lastName: 80,
  position: 160,
  departmentName: 120,
  managerName: 160,
}

/** Zapis nagłówka bez wielkości liter, polskich znaków, spacji i łączników. */
export function normalizeKey(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('pl-PL')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/ł/g, 'l')
    .replace(/[\s-]+/g, '_')
}

const HEADER_ALIASES: ReadonlyMap<string, InvitationImportField> = new Map(
  (
    [
      ['firstName', ['imie', 'first_name', 'firstname']],
      ['lastName', ['nazwisko', 'last_name', 'lastname', 'surname']],
      ['email', ['email', 'e_mail', 'adres_email', 'adres_e_mail', 'mail']],
      ['position', ['stanowisko', 'position']],
      ['departmentName', ['dzial', 'department']],
      ['employmentType', ['rodzaj_umowy', 'umowa', 'contract_type', 'contract']],
      ['startDate', ['data_rozpoczecia', 'data_zatrudnienia', 'start_date']],
      ['managerName', ['przelozony', 'manager']],
    ] as const
  ).flatMap(([field, aliases]) => aliases.map((alias) => [alias, field] as const)),
)

/**
 * Rodzaj umowy: kod z systemu albo nazwa, jaką kadry wpisują w arkuszu.
 * Nazwy porównujemy po normalizacji (bez wielkości liter i polskich znaków).
 */
const CONTRACT_ALIASES: ReadonlyMap<string, ContractType> = new Map([
  ...CONTRACT_TYPES.map((type) => [type, type] as const),
  ['umowa_o_prace_na_czas_nieokreslony', 'uop_nieokreslony'],
  ['uop_czas_nieokreslony', 'uop_nieokreslony'],
  ['czas_nieokreslony', 'uop_nieokreslony'],
  ['umowa_o_prace_na_czas_okreslony', 'uop_okreslony'],
  ['uop_czas_okreslony', 'uop_okreslony'],
  ['czas_okreslony', 'uop_okreslony'],
  ['umowa_na_okres_probny', 'uop_probny'],
  ['okres_probny', 'uop_probny'],
  ['umowa_zlecenia', 'zlecenie'],
  ['umowa_zlecenie', 'zlecenie'],
  ['umowa_o_dzielo', 'dzielo'],
  ['kontrakt_b2b', 'b2b'],
])

export type InvitationImportError =
  | 'required'
  | 'email'
  | 'tooLong'
  | 'contractType'
  | 'date'
  | 'duplicateInFile'
  /* Reguły z bazy — dokłada je serwer. */
  | 'emailTaken'
  | 'invitationPending'

export interface InvitationImportRow {
  /** Numer wiersza w pliku, licząc nagłówek jako 1 — tak, jak widzi go arkusz. */
  readonly line: number
  readonly values: Partial<Record<InvitationImportField, string>>
  readonly errors: Array<{ field: InvitationImportField; code: InvitationImportError }>
}

export type InvitationImportFileError =
  | { code: 'empty' }
  | { code: 'tooLarge' }
  | { code: 'tooManyRows'; max: number }
  | { code: 'missingColumns'; columns: InvitationImportField[] }
  | { code: 'unclosedQuote'; line: number }

export interface InvitationImportParsed {
  readonly rows: InvitationImportRow[]
  readonly fileErrors: InvitationImportFileError[]
  /** Kolumny pliku, których import nie zna — pomijamy je i mówimy o tym. */
  readonly ignoredColumns: string[]
}

/**
 * CSV wg RFC 4180: pola w cudzysłowach (z podwojonym cudzysłowem w środku
 * i końcem wiersza w polu), CRLF albo LF, BOM z Excela. Separator wykrywamy
 * z pierwszego wiersza: polski Excel zapisuje „;”, inne programy „,”.
 */
export function parseCsv(text: string): { rows: string[][]; unclosedQuoteLine: number | null } {
  const source = text.startsWith('﻿') ? text.slice(1) : text
  const firstLine = source.split(/\r?\n/, 1)[0] ?? ''
  const count = (char: string) => firstLine.split(char).length - 1
  const delimiter = count(';') >= count(',') && count(';') > 0 ? ';' : count('\t') > count(',') ? '\t' : ','

  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  let line = 1
  let quoteStartLine = 1
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i]!
    if (quoted) {
      if (char === '"') {
        if (source[i + 1] === '"') {
          field += '"'
          i += 1
        } else {
          quoted = false
        }
      } else {
        if (char === '\n') line += 1
        field += char
      }
      continue
    }
    if (char === '"' && field === '') {
      quoted = true
      quoteStartLine = line
    } else if (char === delimiter) {
      row.push(field)
      field = ''
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && source[i + 1] === '\n') i += 1
      row.push(field)
      rows.push(row)
      row = []
      field = ''
      line += 1
    } else {
      field += char
    }
  }
  if (quoted) return { rows, unclosedQuoteLine: quoteStartLine }
  if (field !== '' || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return { rows, unclosedQuoteLine: null }
}

/** Długość w bajtach UTF-8 — shared nie ma `TextEncoder` (bez lib DOM i Node). */
export function utf8Length(text: string): number {
  let bytes = 0
  for (const char of text) {
    const code = char.codePointAt(0)!
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4
  }
  return bytes
}

/** Data „rrrr-mm-dd” albo „dd.mm.rrrr” — jako dzień kalendarza, który istnieje. */
export function parseImportDate(value: string): string | null {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const dotted = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(value)
  const [year, month, day] = iso
    ? [Number(iso[1]), Number(iso[2]), Number(iso[3])]
    : dotted
      ? [Number(dotted[3]), Number(dotted[2]), Number(dotted[1])]
      : [NaN, NaN, NaN]
  if (!Number.isInteger(year) || month < 1 || month > 12 || day < 1) return null
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  if (day > daysInMonth) return null
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function parseContractType(value: string): ContractType | null {
  return CONTRACT_ALIASES.get(normalizeKey(value).replace(/[·.]/g, '').replace(/_+/g, '_')) ?? null
}

/**
 * Odczyt pliku zaproszeń: wiersze z wartościami gotowymi do zaproszenia
 * (e-mail małymi literami, data w ISO, rodzaj umowy jako kod) i błędami
 * przy polu. Wiersz bez żadnej wartości pomijamy — arkusze kończą się pustymi.
 */
export function parseInvitationImport(text: string): InvitationImportParsed {
  const fileErrors: InvitationImportFileError[] = []
  if (utf8Length(text) > INVITATION_IMPORT_MAX_BYTES) {
    return { rows: [], fileErrors: [{ code: 'tooLarge' }], ignoredColumns: [] }
  }
  const { rows: raw, unclosedQuoteLine } = parseCsv(text)
  if (unclosedQuoteLine !== null) {
    return { rows: [], fileErrors: [{ code: 'unclosedQuote', line: unclosedQuoteLine }], ignoredColumns: [] }
  }
  const [header, ...body] = raw
  if (!header || header.every((cell) => cell.trim() === '')) {
    return { rows: [], fileErrors: [{ code: 'empty' }], ignoredColumns: [] }
  }

  const columns = header.map((cell) => HEADER_ALIASES.get(normalizeKey(cell)) ?? null)
  const ignoredColumns = header.filter((cell, index) => columns[index] === null && cell.trim() !== '')
  const missing = INVITATION_IMPORT_REQUIRED.filter((field) => !columns.includes(field))
  if (missing.length > 0) {
    return { rows: [], fileErrors: [{ code: 'missingColumns', columns: [...missing] }], ignoredColumns }
  }

  const dataRows = body
    .map((cells, index) => ({ cells, line: index + 2 }))
    .filter(({ cells }) => cells.some((cell) => cell.trim() !== ''))
  if (dataRows.length === 0) fileErrors.push({ code: 'empty' })
  if (dataRows.length > INVITATION_IMPORT_MAX_ROWS) {
    fileErrors.push({ code: 'tooManyRows', max: INVITATION_IMPORT_MAX_ROWS })
  }

  const rows: InvitationImportRow[] = dataRows.map(({ cells, line }) => {
    const values: Partial<Record<InvitationImportField, string>> = {}
    const errors: InvitationImportRow['errors'] = []
    columns.forEach((field, index) => {
      const value = (cells[index] ?? '').trim()
      if (field === null || value === '') return
      values[field] = value
    })
    for (const field of INVITATION_IMPORT_REQUIRED) {
      if (!values[field]) errors.push({ field, code: 'required' })
    }
    for (const [field, max] of Object.entries(MAX_LENGTH) as Array<[InvitationImportField, number]>) {
      if ((values[field]?.length ?? 0) > max) errors.push({ field, code: 'tooLong' })
    }
    if (values.email) {
      const email = emailSchema.safeParse(values.email)
      if (email.success) values.email = email.data
      else errors.push({ field: 'email', code: 'email' })
    }
    if (values.employmentType) {
      const type = parseContractType(values.employmentType)
      if (type) values.employmentType = type
      else errors.push({ field: 'employmentType', code: 'contractType' })
    }
    if (values.startDate) {
      const date = parseImportDate(values.startDate)
      if (date) values.startDate = date
      else errors.push({ field: 'startDate', code: 'date' })
    }
    return { line, values, errors }
  })

  /* Ten sam adres dwa razy w pliku — oba wiersze, bo nie wiemy, który jest właściwy. */
  const byEmail = new Map<string, InvitationImportRow[]>()
  for (const row of rows) {
    const email = row.values.email
    if (!email || row.errors.some((error) => error.field === 'email')) continue
    byEmail.set(email, [...(byEmail.get(email) ?? []), row])
  }
  for (const group of byEmail.values()) {
    if (group.length > 1) for (const row of group) row.errors.push({ field: 'email', code: 'duplicateInFile' })
  }

  return { rows, fileErrors, ignoredColumns }
}

/** Szablon pliku: sam nagłówek, separator „;” jak w polskim Excelu, z BOM. */
export function invitationImportTemplate(): string {
  return `﻿${INVITATION_IMPORT_FIELDS.map((field) => INVITATION_IMPORT_HEADERS[field]).join(';')}\r\n`
}
